import React, {useEffect, useCallback, useRef} from 'react';
import {createPortal} from 'react-dom';
import clsx from 'clsx';

interface OverlayProps {
  visible: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  className?: string;
  overlayClassName?: string;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
  preventBodyScroll?: boolean;
  zIndex?: number;
  animationType?: 'fade' | 'slide' | 'zoom' | 'none';
  portal?: boolean;
  portalContainer?: HTMLElement;
  backdropBlur?: boolean;
  position?: 'center' | 'top' | 'bottom';
}

const Overlay: React.FC<OverlayProps> = ({
  visible,
  onClose,
  children,
  className = '',
  overlayClassName = '',
  closeOnClickOutside = false,
  closeOnEscape = true,
  preventBodyScroll = true,
  zIndex = 1000,
  animationType = 'fade',
  portal = true,
  portalContainer,
  backdropBlur = false,
  position = 'center',
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const originalBodyOverflow = useRef<string>('');

  // 处理 body 滚动
  useEffect(() => {
    if (!preventBodyScroll) {
      return;
    }

    if (visible) {
      originalBodyOverflow.current = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = originalBodyOverflow.current;
    }

    return () => {
      if (preventBodyScroll) {
        document.body.style.overflow = originalBodyOverflow.current;
      }
    };
  }, [visible, preventBodyScroll]);

  // 处理 ESC 键
  useEffect(() => {
    if (!closeOnEscape || !visible) {
      return;
    }

    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [visible, closeOnEscape, onClose]);

  // 处理点击外部区域
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (closeOnClickOutside && e.target === e.currentTarget) {
        onClose?.();
      }
    },
    [closeOnClickOutside, onClose],
  );

  // 焦点管理
  useEffect(() => {
    if (!visible) {
      return;
    }

    const activeElement = document.activeElement as HTMLElement;
    const overlay = overlayRef.current;

    if (overlay) {
      overlay.focus();
    }

    return () => {
      if (activeElement && typeof activeElement.focus === 'function') {
        activeElement.focus();
      }
    };
  }, [visible]);

  if (!visible) {
    return null;
  }

  // 动画样式映射
  const getAnimationClasses = () => {
    const baseClasses = 'transition-all duration-300 ease-out';

    switch (animationType) {
      case 'fade':
        return {
          overlay: clsx(baseClasses, 'opacity-0', {'opacity-100': visible}),
          content: '',
        };
      case 'slide':
        return {
          overlay: clsx(baseClasses, 'opacity-0', {'opacity-100': visible}),
          content: clsx(baseClasses, 'transform -translate-y-12', {
            'translate-y-0': visible,
          }),
        };
      case 'zoom':
        return {
          overlay: clsx(baseClasses, 'opacity-0', {'opacity-100': visible}),
          content: clsx(baseClasses, 'transform scale-75', {
            'scale-100': visible,
          }),
        };
      case 'none':
        return {overlay: '', content: ''};
      default:
        return {overlay: '', content: ''};
    }
  };

  // 位置样式映射
  const getPositionClasses = () => {
    switch (position) {
      case 'top':
        return 'justify-center items-start pt-20';
      case 'bottom':
        return 'justify-center items-end pb-20';
      case 'center':
      default:
        return 'justify-center items-center';
    }
  };

  const animationClasses = getAnimationClasses();

  const overlayContent = (
    <div
      ref={overlayRef}
      className={clsx(
        // 基础样式
        'fixed inset-0 flex outline-none',
        // 背景色和模糊效果
        backdropBlur ? 'bg-black/50 backdrop-blur-sm' : 'bg-black/50',
        // 位置
        getPositionClasses(),
        // 动画
        animationClasses.overlay,
        // 自定义 overlay 类
        overlayClassName,
      )}
      style={{zIndex}}
      onClick={handleOverlayClick}
      tabIndex={-1}
      role="dialog"
      aria-modal="true">
      <div
        className={clsx(
          // 基础样式
          'relative size-full',
          // 动画
          animationClasses.content,
          // 自定义内容类
          className,
        )}>
        {children}
      </div>
    </div>
  );

  // 使用 Portal 渲染到指定容器或 body
  if (portal) {
    const container = portalContainer || document.body;
    return createPortal(overlayContent, container);
  }

  return overlayContent;
};

export default Overlay;
